/**
 * looseT — i18n strict-key 例外 helper(凍結前暫時 escape hatch,待回收)。
 * -----------------------------------------------------------------------------
 * ⚠️ 暫時性 helper:i18next v23+ 把 `t` 的 key 綁定成 namespace 的 strict literal
 * union。當某段 i18n key tree 尚未凍結(平行重寫中 / foundation 先補進
 * `*.json` 但型別還沒收),strict key 會編譯不過,需要一個 loose 簽名
 * `(key: string, opts?) => string` 暫時放行動態 key。
 *
 * 此前各域各自 `type LooseT = ...` + `const lt = t as unknown as LooseT` 重複
 * 寫了 4 種拼法(billing/groups `lt`、jobs `looseT`、GroupSwitcher `ltGroups`、
 * home `useLooseHomeT`)。本檔抽成**單一**共用 helper,凍結完成後一次回收即可。
 * **禁止各域再自抽 LooseT cast 製造第 5、6 種拼法** — 一律 import 此檔。
 *
 * 用法(對齊既有最常見的 `lt` 命名):
 * ```ts
 *   const { t } = useTranslation('billing');
 *   const lt = looseT(t);
 *   lt('banner.trialBody', { n: 100 });   // 任意 string key 放行,opts 透傳插值
 * ```
 *
 * 純 type-level cast — runtime 直接回傳同一個 `t`(零包裝、零開銷),只是把
 * strict 簽名換成 loose 簽名。**key 一律取自凍結表 / 已存在的 json,不可拿來
 * 拼任意字串塞使用者輸入**(那是 XSS / 漏翻譯破口)。
 */

/**
 * 寬鬆版 i18n 翻譯函式簽名 —— 吃任意 string key + optional 插值 opts。
 * 採用 repo 內最常見的 `opts` 參數命名(billing / groups / GroupSwitcher 皆此版),
 * 收斂既有 `params?` / key-only 兩個少數變體。
 */
export type LooseT = (key: string, opts?: Record<string, unknown>) => string;

/**
 * 把 strict-typed i18n `t`(react-i18next `useTranslation` 回傳的 `t`)轉成
 * {@link LooseT}。
 *
 * @param tRaw - `useTranslation(ns)` 回傳的 strict `t`(任意 namespace 皆可)。
 * @returns 同一個函式 reference,型別放寬成 {@link LooseT}(零 runtime 包裝)。
 */
export function looseT(tRaw: unknown): LooseT {
  return tRaw as LooseT;
}

/**
 * 寬鬆版「清單型」翻譯函式簽名 —— 讀 `returnObjects: true` 的陣列值。
 *
 * i18next 的 `t(key, { returnObjects: true })` 在 strict 型別下回的是 `string`
 * (簽名沒有為陣列值分支),所以清單型文案(功能卡列表、條款子項…)一定要 cast。
 */
export type LooseListT = <T = string>(key: string) => T[];

/**
 * 把 strict-typed `t` 轉成讀陣列值的 {@link LooseListT}。
 *
 * ⚠️ **要與 `looseT` 放在同一支檔**(見檔頭):各域自己寫
 * `t(key, { returnObjects: true }) as string[]` 就是在製造第 5、6 種拼法,而且
 * 每一處都要自己想清楚 cast 要不要繞 `unknown` —— 收斂成一支之後只需要對一次。
 *
 * ⚠️ **key 必須真的對到一個 JSON 陣列**:對不到時 i18next 回的是字串(通常就是
 * key 本身),`.map()` 不會爆但畫面會顯示一堆單字元 —— 症狀離原因很遠。三語
 * key parity 的測試就是在擋這件事。
 *
 * @param tRaw - `useTranslation(ns)` 回傳的 strict `t`。
 * @returns 吃任意 string key、回陣列的函式(零 runtime 包裝)。
 */
export function looseListT(tRaw: unknown): LooseListT {
  const raw = tRaw as (key: string, opts: Record<string, unknown>) => unknown;
  return <T,>(key: string) => raw(key, { returnObjects: true }) as T[];
}
